import { useEffect, useMemo, useState } from 'react';
import type { Instance, Person } from '@gm-binder/shared';
import { api } from '../../api/client';
import { Modal, Button, Field, Input, Textarea, Switch } from '../ui';
import { toDatetimeLocal, fromDatetimeLocal } from '../../lib/datetime';
import { useRunStore } from '../../store/runStore';
import { useUiStore } from '../../store/uiStore';
import styles from './SessionTools.module.css';

/** "Close the table for the day": capture what happened and pause the run. */
export function CloseDayModal({ instance, onClose }: { instance: Instance; onClose: () => void }) {
  const [people, setPeople] = useState<Person[]>([]);
  const [title, setTitle] = useState(`Sessione ${instance.sessionLog.length + 1}`);
  const [notes, setNotes] = useState('');
  const [when, setWhen] = useState(toDatetimeLocal());
  const [duration, setDuration] = useState('');
  const [pause, setPause] = useState(true);
  const [attendees, setAttendees] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const setInstance = useRunStore((s) => s.setInstance);
  const toast = useUiStore((s) => s.toast);

  useEffect(() => {
    void (async () => {
      try {
        setPeople(await api.listPeople());
      } catch {
        setPeople([]);
      }
    })();
  }, []);

  // Roster people (those linked to a PG in this run) default to "present".
  const rosterPeopleIds = useMemo(
    () => Array.from(new Set(instance.players.map((p) => p.personId).filter(Boolean) as string[])),
    [instance],
  );

  useEffect(() => {
    const init: Record<string, boolean> = {};
    rosterPeopleIds.forEach((id) => {
      init[id] = true;
    });
    setAttendees(init);
  }, [rosterPeopleIds]);

  const rosterPeople = people.filter((p) => rosterPeopleIds.includes(p.id));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.closeInstanceDay(instance.id, {
        title: title.trim() || undefined,
        notes: notes.trim(),
        date: fromDatetimeLocal(when) || undefined,
        durationMin: duration ? Number(duration) : undefined,
        attendees: Object.entries(attendees)
          .filter(([, v]) => v)
          .map(([k]) => k),
        keepActive: !pause,
      });
      setInstance(updated);
      toast(pause ? 'Sessione chiusa e messa in pausa' : 'Sessione registrata', 'success');
      onClose();
    } catch {
      toast('Salvataggio fallito', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="🌙 Chiudi la giornata" width={560}>
      <div className={styles.stack}>
        <p className={styles.hint}>
          Registra cosa è successo in questa sessione. Le note restano nello storico della
          partita per riprendere facilmente la prossima volta.
        </p>

        <Field label="Titolo">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>

        <Field label="Cosa è successo" hint="Eventi chiave, decisioni, cliffhanger…">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            autoFocus
            placeholder="Il gruppo ha sconfitto il guardiano e trovato la mappa…"
          />
        </Field>

        <div className={styles.rosterRow}>
          <Field label="Data">
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
          </Field>
          <Field label="Durata (min)">
            <Input
              type="number"
              min={0}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="180"
            />
          </Field>
        </div>

        {rosterPeople.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Presenti</h3>
            {rosterPeople.map((p) => (
              <label key={p.id} className={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={!!attendees[p.id]}
                  onChange={(e) => setAttendees((a) => ({ ...a, [p.id]: e.target.checked }))}
                />
                {p.name}
              </label>
            ))}
          </div>
        )}

        <Switch checked={pause} onChange={setPause} label="Metti la sessione in pausa" />

        <div className={styles.actions}>
          <span />
          <div className={styles.actionsRight}>
            <Button variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button variant="primary" onClick={() => void save()} disabled={saving}>
              {pause ? 'Chiudi giornata' : 'Registra'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
